import { isAnyArray } from 'is-any-array';
import { addInfoData } from "./utils/addInfoData.js";
import { checkMatrix } from "./utils/checkMatrix.js";
import { checkNumberOrArray } from "./utils/checkNumberOrArray.js";
import { getBestFactor } from "./utils/getBestFactor.js";
import { getBestFactorMatrix } from "./utils/getBestFactorMatrix.js";
import { getExtremeValues } from "./utils/getExtremeValues.js";
import { rescaleAndEnsureInteger } from "./utils/rescaleAndEnsureInteger.js";
import { vectorEncoder } from "./utils/vectorEncoder.js";
/**
 * Create a jcamp of 2D NMR data by variables. Currently only the convertion of processed data
 * is supported. The variables x and y are the direct (F2 in Bruker) and indirect (F1 in bruker),
 * data should be in ppm scale, the z variable is the intensity (dependent)
 * @param variables
 * @param options
 */
export function from2DNMRVariables(variables, options) {
    const { nmrInfo, info = {}, meta = {}, xyEncoding = 'DIFDUP' } = options;
    const factor = 'factor' in options
        ? { ...options.factor }
        : {};
    const { title = '', owner = '', origin = '', dataType = 'NMR SPECTRUM', ...otherInfo } = nmrInfo;
    const symbol = [];
    const variableName = [];
    const variableType = [];
    const variableDim = [];
    const units = [];
    const first = [];
    const last = [];
    const min = [];
    const max = [];
    const factors = [];
    const keys = ['y', 'x', 'z'];
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const variable = variables[key];
        if (!variable)
            throw new Error('variables x, y and z are mandatory');
        const name = variable?.label.replace(/ *\[.*/, '');
        const unit = variable?.label.replace(/.*\[(?<units>.*)\].*/, '$<units>');
        const { firstLast, minMax } = getExtremeValues(variable.data);
        if (!Object.hasOwn(factor, key)) {
            factor[key] = calculateFactor(variable.data, minMax);
        }
        symbol.push(variable.symbol || key);
        variableName.push(name || key);
        variableDim.push(variable.data.length);
        first.push(firstLast.first);
        last.push(firstLast.last);
        max.push(minMax.max);
        min.push(minMax.min);
        factors.push(factor[key]);
        if (variable.isDependent !== undefined) {
            variableType.push(variable.isDependent ? 'DEPENDENT' : 'INDEPENDENT');
        }
        else {
            variableType.push(variable.isDependent !== undefined
                ? !variable.isDependent
                : i === 0
                    ? 'INDEPENDENT'
                    : 'DEPENDENT');
        }
        units.push(variable.units || unit || '');
    }
    let header = `##TITLE=${title}
##JCAMP-DX=6.00
##DATA TYPE=${dataType}
##DATA CLASS= NTUPLES
##ORIGIN=${origin}
##OWNER=${owner}\n`;
    header += addInfoData(info, { prefix: '##' });
    header += addInfoData({ ...otherInfo, ...meta });
    let xData = variables.x.data;
    let yData = variables.y.data;
    checkNumberOrArray(xData);
    checkNumberOrArray(yData);
    checkMandatoryParameters(meta);
    const zData = variables.z?.data || [];
    checkMatrix(zData);
    const { direct, indirect, dependent } = getDimensionIndices(symbol);
    const nuc1 = String(meta.NUC1);
    const nuc2 = String(meta.NUC2);
    const nucleus = new Array(3);
    nucleus[direct] = nuc1;
    nucleus[indirect] = nuc2;
    const sfo1 = Number(meta.SFO1);
    const sfo2 = Number(meta.SFO2);
    const optionsScaleAndJoin = {
        indices: { direct, indirect },
        sfo1,
        sfo2,
    };
    header += `##NTUPLES= ${dataType}
##VAR_NAME=  ${variableName.join(',')}
##SYMBOL=    ${symbol.join(',')}
##VAR_TYPE=  ${variableType.join(',')}
##VAR_DIM=   ${variableDim.join(',')}
##.NUCLEUS=  ${nucleus.join(',')}
##UNITS=     ${units.join(',')}
##FACTOR=    ${factors.join(',')}
##FIRST=     ${scaleAndJoin(first, optionsScaleAndJoin)}
##LAST=      ${scaleAndJoin(last, optionsScaleAndJoin)}
##MIN=       ${scaleAndJoin(min, optionsScaleAndJoin)}
##MAX=       ${scaleAndJoin(max, optionsScaleAndJoin)}\n`;
    header += `##VAR_FORM= AFFN, AFFN, ASDF\n`;
    header += '##NUM DIM= 2\n';
    if (keys[direct] !== 'x') {
        [yData, xData] = [xData, yData];
    }
    const directSymbol = symbol[direct].toUpperCase();
    const indirectSymbol = symbol[indirect].toUpperCase();
    const firstY = yData[0] * sfo2;
    const lastY = yData.at(-1) * sfo2;
    const firstX = xData[0];
    const lastX = xData.at(-1);
    const deltaX = (lastX - firstX) / (xData.length - 1);
    const deltaY = (lastY - firstY) / (yData.length - 1);
    const firstData = new Float64Array(3);
    firstData[direct] = firstX * sfo1;
    firstData[indirect] = firstY;
    const yFactor = factor.y || 1;
    const xFactor = factor.x || 1;
    const zFactor = factor.z || 1;
    for (let index = 0; index < zData.length; index++) {
        const row = zData[index];
        firstData[dependent] = row[0];
        header += `##PAGE= ${indirectSymbol}=${(firstY + deltaY * index) / yFactor}\n`;
        header += `##FIRST=  ${firstData.join(',')}\n`;
        header += `##DATA TABLE= (${directSymbol}++(Y..Y)), PROFILE\n`;
        header += vectorEncoder(rescaleAndEnsureInteger(row, zFactor), firstX / xFactor, deltaX / xFactor, xyEncoding);
        header += '\n';
    }
    return header;
}
function scaleAndJoin(variable, options) {
    const { sfo1, sfo2 } = options;
    const { direct, indirect } = options.indices;
    const copy = variable.slice();
    copy[direct] = copy[direct] * sfo1;
    copy[indirect] = copy[indirect] * sfo2;
    return copy.join(',');
}
function getDimensionIndices(entry) {
    const symbol = entry.map((item) => item.toUpperCase());
    const direct = symbol.includes('F2')
        ? symbol.indexOf('F2')
        : symbol.indexOf('T2');
    const indirect = symbol.includes('F1')
        ? symbol.indexOf('F1')
        : symbol.indexOf('T1');
    if (direct === -1 || indirect === -1) {
        throw new Error('F2/T2 and F1/T1 symbol should be defined for nD NMR SPECTRUM');
    }
    return { direct, indirect, dependent: 3 - direct - indirect };
}
function checkMandatoryParameters(meta) {
    const list = ['SFO1', 'SFO2', 'NUC1', 'NUC2'];
    for (const key of list) {
        const value = meta[key];
        if (!value) {
            throw new Error(`${key} in options.meta should be defined`);
        }
    }
}
function calculateFactor(data, minMax) {
    return isAnyArray(data[0])
        ? getBestFactorMatrix(data, { minMax })
        : getBestFactor(data, { minMax });
}
//# sourceMappingURL=from2DNMRVariables.js.map